import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';
import { bind, next } from '@ember/runloop';
import Component from '@glimmer/component';

import DOMPurify from 'dompurify';
import $ from 'jquery';

import { equal, replaceAll } from 'webapp/utils/common';
import { sanitize } from 'webapp/utils/html';

import themed from 'webapp/decorators/themed';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';

import type { FilterIdType } from 'webapp/models-custom/filter-sort/base';

type ValueSingle = number | string;
type ValueMultiple = number[] | string[] | FilterIdType[];

export type Value = ValueSingle | ValueMultiple;

export interface OptionCommon {
	label2?: string;
	label3?: string;
	onLabel3Click?: () => void;
	label4?: string;
	avatar?: string;
	avatarColor?: string; // coloring svg avatars
	iconName?: IconName;
	iconFill?: string; // coloring ui-icon icons
	scriptId?: number; // TODO: check - possibly unused
	queryId?: number; // TODO: check - possibly unused
	description?: string;
	disabled?: boolean;
	colordroplet?: string;
	color?: string;
	type?: string | number;
	initials?: string;
	prefixClassName?: string;
	prefixIcon?: IconName;
	affixIcon?: IconName;
}

export interface OptionRaw<T> extends OptionCommon {
	value: T;
	label: string;
	hideInDropdown?: boolean;
	hidden?: boolean;
	group?: string;
}

export interface OptionTransformed<T> extends OptionCommon {
	id: T | null; // null only for groups
	text: string;
	hidden?: boolean;
	children?: OptionTransformed<T>[];
	selected?: boolean;
	willBeCreated?: boolean; // when allowing custom add, the current typed is shown as first option with this flag
	element?: HTMLElement; // when using onConnectNewSource hack, a node is passed
	// the following three are used for "special actions" (when extending parseOption)
	isSpecial?: boolean;
	onClick?: (selectElement: JQuery<HTMLElement>) => void;
	html?: string;
}

interface QueryParams {
	term: string;
	page: number;
	callback: (params: { results: object[]; more: boolean }) => void; // select2 query callback
}

export interface CustomEvent {
	target: JQuery<HTMLElement> | Select2Instance;
	event: string;
	fn: ((e: JQuery.TriggeredEvent) => void) | (() => void);
}

// a select2 event as jQuery passes it to handlers; select2:selecting / unselecting carry the option in params
type Select2Event = JQuery.TriggeredEvent & { params?: { args: { data: { id: string } } } };

// the parts of the select2 instance ($el.data('select2')) this component uses
export interface Select2Instance {
	$container: JQuery<HTMLElement>;
	$dropdown: JQuery<HTMLElement>;
	$selection: JQuery<HTMLElement>;
	options: { options: Record<string, unknown> };
	selection: { $search?: JQuery<HTMLInputElement> };
	dropdown: { _positionDropdown(): void };
	dataAdapter: {
		addOptions($options: JQuery<HTMLOptionElement>): void;
		convertToOptions(data: object[]): JQuery<HTMLOptionElement>;
		_checkIfMaximumSelected?(): void; // only with maximumSelectionLength
	};
	isOpen(): boolean;
	trigger(event: string, params?: object): void;
	on(event: string, callback: (...args: never[]) => void): void;
	val(): string | string[] | null; // deprecated in select2, reads the select's value
}

interface ArgsBase<T> {
	// MANDATORY ARGS
	options: OptionRaw<T>[]; // possible choices list, will be transformed with transformOption if passed

	// OPTIONAL ARGS
	// Note: isNumeric and isMultiple are defined on specific interface variants below as discriminants
	isRequired?: boolean; // (default: false)
	isDisabled?: boolean; // (default: false) disables the select control
	isLoading?: boolean; // (default: false) disables the select control and shows loader
	maxSelectionLength?: number; // (default: Infinity) how many options can be selected - works only for `isMultiple` selects
	showLabel2?: boolean; // (default: true) if the value passed as `label2` on an option will be shown
	showLabel3?: boolean; // (default: true) if the value passed as `label3` on an option will be shown
	showFloatlabel?: boolean; // (default: true) if the placeholder will be shown even after value is selected (top left of input) (floatlabel.js library)
	hideLabel3OnSelection?: boolean; // (default: false) if the value passed as `label3` on option will be shown and hidden on selection
	searchInLabel2?: boolean; // (default: false) if we will look for matches in `label2` text that is set on the option
	customAddCaseInsensitive?: boolean; // (default: false = exact match) with `onCustomAdd`, hides the "Add" option when the term matches an existing option ignoring case
	transformOption?: (opt: OptionRaw<T>) => OptionTransformed<T>; // ( default: this.defaultTransformOption) transforms a given option into select2-readable format (`id`, `name`, ..)
	tooltipOnListFromProperty?: string; // (default: '') the property name (after `transformOption` is applied) on the option that will be shown as tooltip. if falsy, is not shown
	tooltipOnSelectionFromProperty?: string; // (default: 'text') the property name (after `transformOption` is applied) on the option that will be shown as tooltip. if falsy, is not shown
	dropdownSearchPlaceholder?: string; // passed directly to select2
	isDropdownWider?: boolean; // flags the dropdown to be wider than the container (f.ex. at format / scale dropdowns)
	placeholder?: string; // placeholder text shown when nothing is selected, and (if `showFloatlabel` is set to true) also in the floatlabel
	floatlabelText?: string; // text shown specifically in the floatlabel (fallback to placeholder)
	extraClass?: string; // string that will be added to the container, dropdown and floatlabel
	onConnectNewSource?: (type: string) => void; // callback that gets passed a string and should trigger connect for that source type
	maxTagsShown?: number; // in input max number of tags is shown other are hidden. Hidden tags are represented with one tag that shows number of hidden elements (+X). Its also dependent on @isMultiple
	selectedTooltipWidth?: number; // width of tooltip(ster) on selected item - when default no max width
	showSelectionTooltipOnlyIfTruncated?: boolean; // (default: false) if true, the tooltip on the selected item is only shown when its text is cut off
	maxOptionsShown?: number; // (default: 200) number of shown options
	additionalResultsText?: string; // (default: '') text to show when no results are found
	hideClearSelection?: boolean; // if true, hides the clear selection button (by default shown for isMultiple selects)
	refreshOpenDropdown?: boolean; // (default: false) multiple selects only: if true, an open dropdown is refit after each change and redrawn when options change (e.g. options disabled at max selection)

	// EVENT HOOKS
	onCustomAdd?: (value: T) => boolean; // if set, allows tagging (entering custom values). this callback triggers on such entry and must return true if the entry is valid and should be added, and false otherwise. The "Add" option is hidden when the trimmed term exactly matches an option's text or id (see `customAddCaseInsensitive`)
	onOpen?: (cb: () => void) => boolean; // callback that is run on dropdown open, and must return true if we want it to be run on the next open again. Gets a callback that will actually open the dropdown. Used to delay open (e.g. loading options)
	onOpening?: (event: JQuery.TriggeredEvent) => void; // callback that is run before dropdown opens and blocks the opening. Used where we want to use the select control without dropdown
	onFocus?: () => void; // callback that is run when the select control gets focus (used in qb)

	theme?: string;
	size?: string;
}

interface ArgsSingle<T> extends ArgsBase<T> {
	selected: T | null | undefined; // Currenty selected options (array for isMultiple selects). Must be numeric for isNumeric selects.
	onChange: (value: T) => void; // Action that will be triggered on selection change. Should update `selected`
	onClose?: (cb: () => void, value?: T) => void; // callback that is run on dropdown close. Gets a callback that will actually close the dropdown.
	beforeChange?: (currentValue: T, newValue: T, selectElement: JQuery<HTMLElement>) => boolean; // callback that gets current value and new value, and can return true if we want to prevent this change
}

interface ArgsMultiple<T> extends ArgsBase<T> {
	selected: T[]; // Currenty selected options (array for isMultiple selects). Must be numeric for isNumeric selects.
	onChange: (value: T[]) => void; // Action that will be triggered on selection change. Should update `selected`
	onClose?: (cb: () => void, value?: T[]) => void; // callback that is run on dropdown close. Gets a callback that will actually close the dropdown.
	beforeChange?: (
		currentValue: T[],
		newValue: T[],
		selectElement: JQuery<HTMLElement>,
	) => boolean; // callback that gets current value and new value, and can return true if we want to prevent this change
}

interface ArgsNumericSingle extends ArgsSingle<number> {
	isNumeric?: true;
	isMultiple?: false;
}

interface ArgsNumericMultiple extends ArgsMultiple<number> {
	isNumeric?: true;
	isMultiple: true;
}

interface ArgsStringSingle extends ArgsSingle<string> {
	isNumeric: false;
	isMultiple?: false;
}

interface ArgsStringMultiple extends ArgsMultiple<string> {
	isNumeric: false;
	isMultiple: true;
}

export type Args = ArgsNumericSingle | ArgsNumericMultiple | ArgsStringSingle | ArgsStringMultiple;

// Helper type to extract the value type (number or string) from Args
type ExtractValueType<M extends Args> = M extends ArgsNumericSingle | ArgsNumericMultiple
	? number
	: M extends ArgsStringSingle | ArgsStringMultiple
		? string
		: never;

export interface FormSelectComponentInterface<M> {
	Element: HTMLSelectElement; // this is needed for ...attributes
	Args: M;
}

// KNOWN PROBLEM:
// In case we are having combination of @isMultiple={{true}} and @isNumeric={{true}} - unselecting items in dropdown is not working.
// HACK: we perform mapping on selected array (string > number > string), also we set @isNumeric={{false}} on Form::Select. Examples can be found on
// share-snapshot-form and notifications-scorecards-form components at scheduleDayOfWeek select. Task: https://app.asana.com/0/7582091858306/1199684179035126/f

@themed()
export default class FormSelectComponent<
	M extends Args,
	T extends number | string = ExtractValueType<M>,
> extends Component<FormSelectComponentInterface<M>> {
	declare size: string; // from @themed
	declare sizeClass: string; // from @themed
	declare theme: string; // from @themed
	declare themeClass: string; // from @themed

	uniqueId = 'select-' + guidFor(this);

	select2SearchBtnClearClass = 'select2-search--btn-search-clear-visible';
	select2ClearSelectionBtnClass = 'select2-clear-selection-btn';
	select2DropdownWithClearClass = 'select2-dropdown--with-clear-selection';

	constructor(owner: Owner, args: M) {
		super(owner, args);

		this.setInitialSelected();
	}

	get isNumeric() {
		return this.args.isNumeric !== false;
	}

	get isMultiple() {
		return this.args.isMultiple ?? false;
	}

	get isDisabled() {
		return !!this.args.isDisabled || !!this.args.isLoading;
	}

	get maxSelectionLength() {
		return this.isMultiple ? this.args.maxSelectionLength || Infinity : Infinity;
	}

	get showLabel2() {
		return this.args.showLabel2 !== false;
	}

	get showLabel3() {
		return this.args.showLabel3 !== false;
	}

	get showFloatlabel() {
		return this.args.showFloatlabel !== false;
	}

	get tooltipOnListFromProperty() {
		return this.args.tooltipOnListFromProperty ?? '';
	}

	get tooltipOnSelectionFromProperty() {
		return this.args.tooltipOnSelectionFromProperty ?? 'text';
	}

	get hasCustomTooltipOnSelection() {
		return !this.isMultiple && this.args.tooltipOnSelectionFromProperty;
	}

	get isMultipleOrCustomAdd() {
		// this property only affects select2 script, not value selection
		// if we do not have multiple on the select, it does not allow to add custom value
		return !!this.isMultiple || !!this.args.onCustomAdd;
	}

	get showEmptyPlaceholder() {
		return this.args.placeholder && !this.isMultiple;
	}

	get extraClass() {
		return this.args.extraClass;
	}

	get onOpening() {
		return this.args.onOpening;
	}

	get maxTagsShown() {
		return this.args.maxTagsShown;
	}

	get selectedTooltipWidth() {
		return this.args.selectedTooltipWidth ?? null;
	}

	get maxOptionsShown() {
		return this.args.maxOptionsShown || 200;
	}

	get floatlabelText(): string {
		// used for floatlabel
		return this.args.floatlabelText || this.args.placeholder || '';
	}

	get showClearSelection() {
		return this.isMultiple && !this.args.hideClearSelection;
	}

	private isArgsNumericSingle(_args: Args): _args is ArgsNumericSingle {
		return !this.isMultiple && this.isNumeric;
	}

	private isArgsNumericMultiple(_args: Args): _args is ArgsNumericMultiple {
		return this.isMultiple && this.isNumeric;
	}

	private isArgsStringSingle(_args: Args): _args is ArgsStringSingle {
		return !this.isMultiple && !this.isNumeric;
	}

	private isArgsStringMultiple(_args: Args): _args is ArgsStringMultiple {
		return this.isMultiple && !this.isNumeric;
	}

	// --------- preparing options START --------

	private _options: OptionTransformed<T>[] | null = null;

	private optionsHasGroups = false;

	// options are cached and only updated when args.options change (with _parseOptions)
	get options() {
		if (!this._options) {
			// eslint-disable-next-line ember/no-side-effects
			this._options = this._parseOptions(this.args.options as OptionRaw<T>[]);
		}
		return this._options;
	}

	set options(value: OptionTransformed<T>[]) {
		this._options = value;
	}

	_parseOptions(optionsRaw: OptionRaw<T>[]): OptionTransformed<T>[] {
		const hasGroups = optionsRaw.some((opt) => !!opt.group);
		let options: OptionTransformed<T>[] = [];

		this.optionsHasGroups = hasGroups;

		(optionsRaw || []).forEach((option) => {
			const optionData = this.transformOption(option);

			if (hasGroups) {
				// grouped view, look for the correct group
				const group = options.find((opt) => opt.text === option.group);

				if (group) {
					// group already exists
					group.children?.push(optionData);
				} else {
					options.push({
						id: null,
						text: option.group || '',
						children: [optionData],
					});
				}
			} else {
				// just a listing
				options.push(optionData);
			}
		});

		if (hasGroups) {
			// Other connected and Not connected at the bottom
			const connectedGroup = options.find((opt) => opt.text === 'Other connected sources');
			if (connectedGroup) {
				options = options.filter((o) => o !== connectedGroup);
				options.push(connectedGroup);
			}

			const notConnectedGroup = options.find((opt) => opt.text === 'Not connected');
			if (notConnectedGroup) {
				options = options.filter((o) => o !== notConnectedGroup);
				options.push(notConnectedGroup);
			}

			// hide those that have all children hidden
			options
				.filter((o) => {
					return !(o.children || []).some((c) => {
						return !c.hidden;
					});
				})
				.forEach((o) => {
					o.hidden = true;
				});
		}

		return options;
	}

	transformOption(option: OptionRaw<T>): OptionTransformed<T> {
		// must transform input options into select2 format (id, text)
		const transformed =
			typeof this.args.transformOption === 'function'
				? (
						this.args.transformOption as unknown as (
							opt: OptionRaw<T>,
						) => OptionTransformed<T>
					)(option)
				: this.defaultTransformOption(option);

		// parse for isNumber
		// select element itself uses string values but options are used for other calculations too
		if (this.isNumeric && typeof option.value !== 'number' && transformed.id !== null) {
			transformed.id = +transformed.id as T;
		} else if (!this.isNumeric && typeof option.value !== 'string') {
			transformed.id = String(transformed.id) as T;
		}

		return transformed;
	}

	formatResult(item: OptionTransformed<T>, isOnList = false): JQuery<HTMLElement> | '' {
		if (!item?.text) {
			return '';
		}

		let innerHtml = sanitize(item.text);

		if (item.isSpecial) {
			const html = $(item.html || '').on(
				'click',
				bind(this, () => {
					if (item.onClick) {
						item.onClick(this._selectElement!);
					}
					this._selectElement?.select2('close');
					return false;
				}),
			);
			return $('<div class="base"></div>').append(html);
		}

		const hasExtra = this.showLabel3 && item.label3;

		let hasAvatar = false;
		if (item.avatar) {
			hasAvatar = true;
			const avatarColorStyle = item.avatarColor ? ` style="${item.avatarColor}"` : '';
			innerHtml =
				'<span class="avatar-wrapper"><img class="avatar" src="' +
				item.avatar +
				'"' +
				avatarColorStyle +
				'/></span>' +
				'<span class="text">' +
				innerHtml +
				'</span>';

			if (item.prefixIcon) {
				innerHtml =
					'<ui-icon name="' + item.prefixIcon + '" size="sm"></ui-icon>' + innerHtml;
			} else if (item.prefixClassName) {
				innerHtml = '<span class="' + item.prefixClassName + '"></span>' + innerHtml;
			}
		} else if (item.colordroplet) {
			hasAvatar = true;
			innerHtml =
				(item.colordroplet === 'stripes'
					? '<span class="icon stripes"></span>'
					: '<ui-icon name="RaindropFill" size="sm" fill="' +
						item.colordroplet +
						'"></ui-icon>') + innerHtml;
		} else if (item.color) {
			hasAvatar = true;
			innerHtml =
				'<span class="circle" style="background-color:' +
				item.color +
				'"></span>' +
				innerHtml;
		} else if (item.initials) {
			hasAvatar = true;
			innerHtml = '<span class="initials">' + item.initials + '</span>' + innerHtml;
		} else if (item.prefixIcon) {
			hasAvatar = item.prefixIcon !== 'ArrowDownRight';
			innerHtml =
				'<ui-icon name="' +
				item.prefixIcon +
				'" size="sm"></ui-icon>' +
				'<span class="text">' +
				innerHtml +
				'</span>';
		} else if (item.prefixClassName) {
			innerHtml =
				'<span class="' +
				item.prefixClassName +
				'"></span>' +
				'<span class="text">' +
				innerHtml +
				'</span>';
		} else if (item.iconName) {
			innerHtml =
				'<ui-icon name="' +
				item.iconName +
				`" fill="${item.iconFill || 'currentcolor'}"` +
				'></ui-icon>' +
				'<span class="text">' +
				innerHtml +
				'</span>';
		}

		if (item.affixIcon) {
			innerHtml =
				innerHtml +
				'<ui-icon name="' +
				item.affixIcon +
				'" size="sm" class="affix"></ui-icon>';
		}

		innerHtml =
			'<span class="label' + (hasAvatar ? ' hasAvatar' : '') + '">' + innerHtml + '</span>';

		if (!this.args.onCustomAdd && this.showLabel2 && item.label2) {
			innerHtml += '<span class="label2">' + sanitize(item.label2) + '</span>';
		}

		const baseExtraClassNames: string[] = [];

		// Appending button '+ Add'
		if (this.args.onCustomAdd && isOnList && item.willBeCreated && item.label2) {
			const createOptionClassName = 'create-option',
				createOptionSmallClassName = `${createOptionClassName}--small`;

			baseExtraClassNames.push(createOptionClassName);
			const containerWidth = this._selectInstance?.$container[0]!.offsetWidth;
			if (containerWidth !== undefined && containerWidth < 180) {
				baseExtraClassNames.push(createOptionSmallClassName);
			}

			innerHtml += `<a role="button" class="button size-tiny">+ <span>${sanitize(item.label2)}</span></a>`;
		}

		// Appending clear btn
		const isEligibleForClearBtn =
			(!this.args.isRequired || this.isMultiple) &&
			!item?.children && // prevent appending to groups
			isOnList;

		if (isEligibleForClearBtn) {
			baseExtraClassNames.push('selection-clear-btn');
		}

		const baseExtraClass = baseExtraClassNames.join(' ');
		innerHtml = `<div class="base ${baseExtraClass}">` + innerHtml + '</div>';

		if (isEligibleForClearBtn) {
			innerHtml += '<ui-icon name="Close" size="xs"></ui-icon>';
		}

		const classes: string[] = [];
		if (item.type) {
			classes.push('type-' + item.type);
		}

		if (hasExtra) {
			const shouldShowLabel3 = !this.args.hideLabel3OnSelection || isOnList;

			if (shouldShowLabel3) {
				classes.push('has-extra');
				innerHtml =
					'<div class="extra"><span class="label3">' +
					sanitize(item.label3 || '') +
					'</span></div>' +
					innerHtml;
			}
		}

		let tooltip = '';
		if (isOnList && this.tooltipOnListFromProperty) {
			tooltip = item[this.tooltipOnListFromProperty as keyof OptionTransformed<T>] as string;
		} else if (!isOnList && this.tooltipOnSelectionFromProperty) {
			tooltip = item[
				this.tooltipOnSelectionFromProperty as keyof OptionTransformed<T>
			] as string;
		}
		tooltip = sanitize(tooltip);

		let titleTooltipText = '';
		if (tooltip && isOnList) {
			tooltip = this.getUnescapedText(tooltip); // need to get tags back, but leave " as `&quot;`

			titleTooltipText = replaceAll(tooltip, '<br>', '\n');
			titleTooltipText = DOMPurify.sanitize(titleTooltipText, { ALLOWED_TAGS: [] }); // remove all tags for os tooltip
			titleTooltipText = this.getEscapedText(titleTooltipText); // get back `&quot;` for "
		}

		const html =
			'<div' +
			` data-id="${String(item.id)}"` + // we need this for select-attributes-with-color
			' class="tooltip' +
			(classes.length ? ' ' + classes.join(' ') : '') +
			'"' +
			(tooltip ? ` data-tooltip="${tooltip}"` : '') +
			(titleTooltipText !== null ? ' title="' + titleTooltipText + '"' : '') +
			'>' +
			innerHtml +
			'</div>';

		// hack - only for designer-panels-metrics
		const onConnectNewSource = this.args.onConnectNewSource;
		if (
			onConnectNewSource &&
			item.element?.nodeName === 'OPTGROUP' &&
			item.text !== 'Not connected' &&
			item.text !== 'Custom' &&
			item.text !== 'Other connected sources'
		) {
			const customHtml = $('<a />');
			customHtml.addClass('connect-button');
			customHtml.html('+ Add');
			customHtml.on('click', () => {
				onConnectNewSource(item.text);
				this._selectElement?.select2('close');
			});
			return $(html).add(customHtml);
		} else {
			return $(html);
		}
	}

	private findInOptionsById(id: T) {
		let option;

		const options = this.options;
		if (this.optionsHasGroups) {
			options.some((group) => {
				option = (group.children || []).find((opt) => opt.id === id);
				return !!option;
			});
		} else {
			option = options.find((opt) => opt.id === id);
		}

		return option;
	}

	private getEscapedText(text: string) {
		return (text || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	private getUnescapedText(text: string) {
		return (text || '').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
	}

	protected defaultTransformOption(option: OptionRaw<T>): OptionTransformed<T> {
		const normalizedOption: OptionTransformed<T> = {
			id: option.value,
			text: this.getEscapedText(option.label),
			label2: option.label2,
			label3: option.label3,
			label4: option.label4,
			avatar: option.avatar,
			scriptId: option.scriptId,
			queryId: option.queryId,
			description: this.getEscapedText(option.description || ''), // need to escape also because of "
		};

		if (option.disabled) {
			normalizedOption.disabled = true;
		}
		if (option.colordroplet) {
			normalizedOption.colordroplet = option.colordroplet;
		}
		if (option.prefixClassName) {
			normalizedOption.prefixClassName = option.prefixClassName;
		}
		if (option.prefixIcon) {
			normalizedOption.prefixIcon = option.prefixIcon;
		}
		if (option.color) {
			normalizedOption.color = option.color;
		}
		if (option.type === 'special') {
			normalizedOption.type = option.type;
		}
		if (option.hideInDropdown || option.hidden) {
			normalizedOption.hidden = true;
		}
		if (option.initials) {
			normalizedOption.initials = option.initials;
		}
		if (option.avatarColor) {
			normalizedOption.avatarColor = option.avatarColor;
		}
		if (option.affixIcon) {
			normalizedOption.affixIcon = option.affixIcon;
		}
		if (option.iconName) {
			normalizedOption.iconName = option.iconName;
		}
		if (option.iconFill) {
			normalizedOption.iconFill = option.iconFill;
		}

		return normalizedOption;
	}

	// --------- preparing options END --------

	// -------- filter and search START -------

	query({ term = '', page = 1, callback }: QueryParams) {
		let results: OptionTransformed<T>[] = [];

		const options = this.options,
			uTerm = term.trim().toUpperCase();

		if (uTerm) {
			options.forEach((opt) => {
				// can modify grouped options (children)
				const resultSet = this.searchMatch(uTerm, opt);
				if (resultSet) {
					results.push(resultSet);
				}
			});

			if (this.args.onCustomAdd) {
				const customTerm = term.trim(),
					normalize = (value: unknown, ignoreCase: boolean) =>
						ignoreCase ? String(value).toUpperCase() : String(value),
					matchesTerm = (opt: OptionTransformed<T>, ignoreCase: boolean) => {
						const compareTerm = normalize(customTerm, ignoreCase);
						return (
							normalize(opt.text, ignoreCase) === compareTerm ||
							normalize(opt.id, ignoreCase) === compareTerm
						);
					};

				if (
					!results.some((opt) => matchesTerm(opt, !!this.args.customAddCaseInsensitive))
				) {
					const customOption = {
						id: customTerm as T,
						text: customTerm,
						willBeCreated: true,
						label2: 'Add',
					};

					// a term differing from an option only by case goes after that option, so it stays highlighted for Enter
					if (results.some((opt) => matchesTerm(opt, true))) {
						results.push(customOption);
					} else {
						results.unshift(customOption);
					}
				}
			}
		} else {
			results = options;
		}

		// sort the results by first word if term is present
		if (uTerm) {
			options
				.filter((item) => item.isSpecial)
				.forEach((item) => {
					if (!results.includes(item)) {
						results.push(item);
					}
				});
			results = this.searchSort(results, uTerm.split(' ')[0] || '');
		}

		const pageSize = this.maxOptionsShown;
		// cut off results for performance purposes, but always keep special ones
		results = results.slice((page - 1) * pageSize, page * pageSize);

		options
			.filter((item) => item.isSpecial)
			.forEach((item) => {
				if (!results.includes(item)) {
					results.push(item);
				}
			});

		callback({
			results: results,
			more: results.length >= page * pageSize,
		});
	}

	private searchMatch(term: string, option: OptionTransformed<T>) {
		if (!term) {
			return option;
		}

		const searchInLabel2 = this.args.searchInLabel2,
			termWords = term.split(' ');

		const matchingFn = (opt: OptionTransformed<T>) => {
			const text = String(opt.text).toUpperCase(),
				label2 = String(opt.label2).toUpperCase();
			return termWords.every((term) => {
				return text.includes(term) || (searchInLabel2 && label2.includes(term));
			});
		};

		// if parent matches, return all children
		// if parent does not match, return only children that match
		// if parent does not match and no children match, skip result
		if (option.isSpecial || matchingFn(option)) {
			return option;
		}

		if (option.children) {
			const modifiedData = Object.assign({}, option);

			modifiedData.children = (modifiedData.children || []).filter(matchingFn);

			if (modifiedData.children.length > 0) {
				return modifiedData;
			}
		}

		return null;
	}

	private searchSort(results: OptionTransformed<T>[], term: string) {
		results.sort((a, b) => {
			const indexInIdA = String(a.id).toUpperCase().indexOf(term),
				indexInTextA = String(a.text).toUpperCase().indexOf(term),
				indexInIdB = String(b.id).toUpperCase().indexOf(term),
				indexInTextB = String(b.text).toUpperCase().indexOf(term);

			// -1 : 'a' comes first
			// +1 : 'b' comes first
			//  0 : whatevers

			if (a.isSpecial) {
				return -1;
			}
			if (b.isSpecial) {
				return 1;
			}

			// present in both texts, just compare indices
			if (indexInTextA > -1 && indexInTextB > -1) {
				if (indexInTextA > indexInTextB) {
					return 1;
				} else if (indexInTextA < indexInTextB) {
					return -1;
				} else {
					// if the same indices, return the one with shortest length first
					if (a.text.length === b.text.length) {
						return 0;
					} else {
						return a.text.length < b.text.length ? -1 : 1;
					}
				}
			}

			// present in one text -> that comes first
			if (indexInTextA > -1) {
				return -1;
			}
			if (indexInTextB > -1) {
				return 1;
			}

			// present in both ids, just compare indices
			if (indexInIdA > -1 && indexInIdB > -1) {
				return indexInIdA < indexInIdB ? -1 : indexInIdA === indexInIdB ? 0 : 1;
			}

			// shouldn't really come to this
			return 0;
		});

		return results;
	}

	// -------- filter and search END -------

	// -------- tooltip setup START ---------

	private tooltipInstance: TooltipsterInstance | null = null;

	private _tooltipTextForSelection: string | false | null = null;

	get tooltipTextForSelection() {
		if (!this._tooltipTextForSelection && this._tooltipTextForSelection !== false) {
			const selectedId = this.args.selected,
				selectedLabels = [];

			if (selectedId) {
				const option = this.findInOptionsById(selectedId as T);

				if (option) {
					const formatted = this.formatResult(option);
					if (formatted) {
						let title =
							(formatted.data('tooltip') as string | number | undefined) ||
							formatted.find('.base').text();
						if (formatted.find('.extra .warn').attr('title')) {
							title += ' (' + formatted.find('.extra .warn').attr('title') + ')';
						}
						selectedLabels.push(title);
					}
				}
			}

			// eslint-disable-next-line ember/no-side-effects
			this._tooltipTextForSelection = sanitize(selectedLabels.join(', ')) || false;
		}

		return this._tooltipTextForSelection;
	}

	setupTooltipOnSelection() {
		if (!this._selectElement) {
			return;
		}

		const dropdown = this._selectInstance!.$container;

		if (!dropdown.length) {
			return;
		}

		dropdown.tooltipster({
			contentAsHTML: true,
			content: this.tooltipTextForSelection,
			delay: 500,
			restoration: 'none',
			theme: 'tooltipster-default',
			position: 'bottom',
			animationDuration: 200,
			maxWidth: this.selectedTooltipWidth,
			functionBefore: (instance: TooltipsterInstance /*, helper*/) => {
				const tooltipText = this.tooltipTextForSelection;
				if (!tooltipText) {
					return false;
				}
				if (this.args.showSelectionTooltipOnlyIfTruncated) {
					const label = dropdown.find('.select2-selection__rendered .base').get(0);
					if (label && label.scrollWidth <= label.clientWidth) {
						return false;
					}
				}
				instance.content(tooltipText);
				return;
			},
		});

		this.tooltipInstance = dropdown.tooltipster('instance');
	}

	private resetTooltipTextForSelectionCache() {
		// trigger tooltip text update for next get
		this._tooltipTextForSelection = null;
	}

	// -------- tooltip setup END ---------

	// ----- general setup and teardown START -------

	_selectElement: JQuery<HTMLElement> | null = null;

	_selectInstance: Select2Instance | null = null;

	@action
	setupSelect(node: HTMLElement) {
		this._selectElement = $(node);

		// select2
		const opts = {
			data: this.options,
			theme: this.themeCssClasses,
			placeholder: this.args.placeholder,
			dropdownSearchPlaceholder: this.args.dropdownSearchPlaceholder,
			multiple: this.isMultiple,
			tags: !!this.args.onCustomAdd,
			resultTabs: null, // TODO: remove this also from select2
			closeOnSelect: !this.isMultiple,
			enableHorizontalScroll: true,
			scrollAfterSelect: !this.isMultiple,
			minimumResultsForSearch: this.args.onCustomAdd ? 0 : 15,
			maximumSelectionLength: this.maxSelectionLength,
			language: {
				maximumSelected: () => {
					return $(
						`<span class='message'>Maximum ${this.maxSelectionLength} items selected</span>`,
					);
				},
				noResults: () => {
					const defaultText = 'No results found.';
					const additionalText = this.args.additionalResultsText;

					if (additionalText) {
						return `${defaultText} ${additionalText}`;
					}
					return defaultText;
				},
				emptyResults: () => {
					const defaultText = 'No options available.';
					const additionalText = this.args.additionalResultsText;

					if (additionalText) {
						return `${defaultText} ${additionalText}`;
					}
					return defaultText;
				},
			},
			templateSelection: (item: OptionTransformed<T>) => {
				return this.formatResult(item);
			},
			templateResult: (item: OptionTransformed<T>) => {
				return this.formatResult(item, true);
			},
			query: (queryData: QueryParams) => {
				this.query(queryData);
			},
		};

		this._selectElement.select2(opts);

		this._selectInstance = this._selectElement.data('select2') as Select2Instance;

		this._themeCssClasses = this.themeCssClasses;

		// replace font icon with UiIcon in dropdown arrow
		// the .icon-dropdown-selector is created by the databox-select2 package.
		// this cleanup code can be removed once that package is updated to use ui-icon.
		const arrowContainer = this._selectInstance?.$container.find('.select2-selection__arrow');
		if (arrowContainer?.length) {
			arrowContainer.find('.icon-dropdown-selector').remove();
			arrowContainer.append('<ui-icon name="ChevronDown" size="sm"></ui-icon>');
		}

		this.setupCustomEvents();

		// tooltipster
		if (this.hasCustomTooltipOnSelection) {
			this.setupTooltipOnSelection();
		}

		// floatlabel
		if (this.showFloatlabel && this.size !== 'small') {
			this._selectElement.floatlabel();
		}

		// show limited number of tags
		if (this.isMultiple && this.maxTagsShown) {
			this.updateShownTags();
		}
	}

	@action
	teardownSelect() {
		this.teardownCustomEvents();

		// tooltipster
		if (this.tooltipInstance) {
			this.tooltipInstance.destroy();
		}

		// select2
		if (this._selectElement) {
			this._selectElement.select2('close');
			this._selectElement.select2('destroy');
		}
	}

	@action
	recreateSelect() {
		// triggered on maxSelectionLength change to avoid wrong error messages
		// can add other use-cases later

		const currentOptions = this._selectInstance?.options?.options;
		const mustRecreate =
			currentOptions?.maximumSelectionLength !== this.args.maxSelectionLength;

		if (mustRecreate) {
			this.teardownSelect();
			const node = this._selectElement?.get(0);
			if (node) {
				this.setupSelect(node);
				this._selectElement?.trigger('blur'); // so floatlabels.js plugin calls hide / show labels
			}
		}
	}

	initialSelection: OptionTransformed<T>[] = [];

	private setInitialSelected() {
		const initialSelection: OptionTransformed<T>[] = [];

		let selectedArray: Value[] = [];
		if (this.isMultiple) {
			const selectedAsArray = this.args.selected as ValueMultiple;
			if (selectedAsArray?.length) {
				// still could be null
				selectedArray = selectedAsArray;
			}
		} else {
			const selected = this.args.selected as ValueSingle;
			if (selected || selected === 0) {
				selectedArray = [selected];
			}
		}

		selectedArray.forEach((id) => {
			const item = this.findInOptionsById(id as T);
			if (item) {
				initialSelection.push(item);
			}
		});

		this.initialSelection = initialSelection; // only used at initial rendering

		// initialSelection.forEach((item) => {
		// 	var newOption = new Option(item.text, item.id, true, true);
		// 	this._selectElement.append(newOption);
		// });
	}

	private _customEvents: CustomEvent[] = [];

	protected setupCustomEvents(): CustomEvent[] {
		const customEvents: CustomEvent[] = [],
			el = this._selectElement,
			selectInstance = this._selectInstance;

		if (!el || !selectInstance) {
			return [];
		}

		let handlerFn;

		handlerFn = (e: JQuery.TriggeredEvent) => {
			const dropdown = selectInstance?.$dropdown.find('.select2-dropdown');

			if (this.args.isDropdownWider && !dropdown.find('.overlay').length) {
				const overlay = $('<span/>').addClass('overlay');
				dropdown?.prepend(overlay);
			}

			// to reset visibility of `searchBtnClearEl`
			selectInstance?.$dropdown.removeClass(this.select2SearchBtnClearClass);

			this.setupSearchClearBtn();
			if (this.showClearSelection) {
				this.setupClearSelectionBtn();
			}

			next(this, this.updateDropdownSizes, e);
		};

		customEvents.push({
			target: el,
			event: 'select2:open',
			fn: handlerFn,
		});

		handlerFn = () => {
			// to reset max height that was set in updateDropdownSizes - to prevent jumping of window scroll when dropdown is stretched to max
			const options = selectInstance?.$dropdown.find(
				'.select2-results > .select2-results__options',
			);

			if (options) {
				options.css({ maxHeight: '0px' });
			}
		};

		customEvents.push({
			target: el,
			event: 'select2:close',
			fn: handlerFn,
		});

		const onOpenFn = this.args.onOpen;
		if (onOpenFn) {
			let doBlock = true; // so it doesn't fall into an infinite loop

			handlerFn = (e: JQuery.TriggeredEvent) => {
				if (!doBlock) {
					return;
				}
				// if callback explicitly returns truthy value
				// we need to rerun this every time dropdown opens
				// var instead of let on purpose to avoid uninitialized variable access
				// when callback is executed immediately (e.g for attribute query on generic metrickey)
				// eslint-disable-next-line no-var
				var runOnReopen = onOpenFn(() => {
					doBlock = false;
					if (this._selectInstance) {
						try {
							// https://rollbar.com/databox/Webapp/items/14767
							this._selectElement?.select2('open');
						} catch (_e) {
							// skip
						}
						next(() => {
							try {
								// focus search after open
								selectInstance.selection.$search!.focus();
							} catch (_e) {
								// skip
							}
						});
					}
					doBlock = !!runOnReopen;
				});
				e.preventDefault();
			};

			customEvents.push({
				target: el,
				event: 'select2:open',
				fn: handlerFn,
			});
		}

		// the single and multiple onClose differ only in the value type
		const onCloseFn = this.args.onClose as
			((cb: () => void, value?: T | T[] | null) => void) | undefined;
		if (onCloseFn) {
			handlerFn = () => {
				onCloseFn(
					() => {
						this._selectElement?.select2('close');
					},
					selectInstance?.val() as T | T[] | null | undefined,
				);
			};

			customEvents.push({
				target: el,
				event: 'select2:close',
				fn: handlerFn,
			});
		}

		const onOpeningFn = this.onOpening;
		if (onOpeningFn) {
			handlerFn = (e: JQuery.TriggeredEvent) => {
				this.tooltipInstance?.close();
				onOpeningFn(e);
				e.preventDefault();
				return false;
			};

			customEvents.push({
				target: el,
				event: 'select2:opening',
				fn: handlerFn,
			});
		}

		if (this.args.onFocus) {
			customEvents.push({
				target: selectInstance,
				event: 'focus',
				fn: this.args.onFocus,
			});
		}

		if (this.isMultiple) {
			handlerFn = () => {
				selectInstance?.$selection.trigger('clear.search');
			};

			customEvents.push({
				target: el,
				event: 'select2:selecting',
				fn: handlerFn,
			});
		}

		if (this.isMultiple && this.args.refreshOpenDropdown) {
			// tags change the selection height (and the max message may appear), so the open dropdown must be refit
			handlerFn = () => {
				next(this, this.updateDropdownSizes);
			};

			customEvents.push({
				target: el,
				event: 'change.select2',
				fn: handlerFn,
			});
		}

		if (this.isMultiple && this.maxTagsShown) {
			handlerFn = () => {
				this.updateShownTags();
			};

			customEvents.push({
				target: el,
				event: 'change.select2',
				fn: handlerFn,
			});
		}

		if (this.showClearSelection) {
			handlerFn = () => {
				this.setupClearSelectionBtn();
			};

			customEvents.push({
				target: el,
				event: 'change.select2',
				fn: handlerFn,
			});
		}

		const beforeChangeFn = this.args.beforeChange;
		if (beforeChangeFn) {
			handlerFn = (e: JQuery.TriggeredEvent) => {
				let currentValue = el?.val() as Value,
					newValue = (e as Select2Event).params?.args.data.id as Value;

				if (this.isNumeric) {
					currentValue = +currentValue;
					newValue = +newValue;
				}

				const doPrevent = (
					beforeChangeFn as (
						currentValue: Value,
						newValue: Value,
						selectElement: JQuery<HTMLElement>,
					) => boolean
				)(currentValue, newValue, el);

				if (doPrevent) {
					e.preventDefault();
				}
			};

			customEvents.push({
				target: el,
				event: 'select2:selecting',
				fn: handlerFn,
			});
		}

		if (this.hasCustomTooltipOnSelection) {
			handlerFn = () => {
				this.tooltipInstance?.disable();
			};

			customEvents.push({
				target: el,
				event: 'select2:open',
				fn: handlerFn,
			});

			handlerFn = () => {
				this.tooltipInstance?.enable();
				this.tooltipInstance?.content(this.tooltipTextForSelection);
			};

			customEvents.push({
				target: el,
				event: 'select2:close',
				fn: handlerFn,
			});
		}

		const onCustomAddFn = this.args.onCustomAdd;
		if (onCustomAddFn) {
			handlerFn = (e: JQuery.TriggeredEvent) => {
				const id = (e as Select2Event).params?.args.data.id,
					isCustomAdded = id && !this.options.find((opt) => opt.id === id);

				if (isCustomAdded) {
					const isValid = (onCustomAddFn as (value: T) => boolean)(id as T);
					if (!isValid) {
						e.preventDefault();
					}
				}
			};

			customEvents.push({
				target: el,
				event: 'select2:selecting',
				fn: handlerFn,
			});
		}

		// save them for unbind later
		this._customEvents = customEvents;

		customEvents.forEach(({ target, event, fn }) => {
			// the select2 instance has its own on(), separate from jQuery's
			if ('length' in target) {
				target.on(event, fn);
			} else {
				target.on(event, fn);
			}
		});

		return customEvents;
	}

	private teardownCustomEvents() {
		if (this._customEvents) {
			this._customEvents.forEach(({ target, event, fn }) => {
				// checking if target exists (e.g. when closing querybuilder cloneable row target doesn't exist)
				if ('length' in target && target.length) {
					target.off(event, fn);
				}
			});
		}
	}

	// ----- general setup and teardown END -------

	// ------- css class updating START --------

	private _themeCssClasses = '';

	get themeCssClasses() {
		const classes: string[] = [];

		classes.push(this.themeClass);

		if (this.sizeClass) {
			classes.push(this.sizeClass);
		}

		if (this.extraClass) {
			classes.push(this.extraClass);
		}

		if (this.args.onCustomAdd) {
			if (this.isMultiple) {
				classes.push('multiCombo');
			} else {
				classes.push('singleCombo');
			}
		}

		if (this.args.isDropdownWider) {
			classes.push('dropdown-wider');
		}

		if (this.args.isLoading) {
			classes.push('show-loader');
		}

		if (this.isMultiple && this.maxTagsShown) {
			classes.push('limited-tags');
		}

		// starting space so select2 does not prepend its prefix
		return ' ' + classes.join(' ');
	}

	@action
	updateThemeCssClasses() {
		const classes = this.themeCssClasses,
			prevClasses = this._themeCssClasses;

		if (prevClasses !== classes) {
			const container = this._selectInstance?.$container;
			if (container) {
				container.removeClass(prevClasses);
				container.addClass(classes);
			}

			const dropdown = this._selectInstance?.$dropdown;
			if (dropdown) {
				dropdown.removeClass(prevClasses);
				dropdown.addClass(classes);
			}

			// updating floatlabel, which has class names in format "floatlabel-for-class-*"
			const floatlabel = this._selectElement?.closest('.floatlabel-for-select');
			if (floatlabel) {
				if (prevClasses) {
					prevClasses.split(' ').forEach((className) => {
						if (className) {
							floatlabel.removeClass(`floatlabel-for-class-${className}`);
						}
					});
				}
				classes.split(' ').forEach((className) => {
					if (className) {
						floatlabel.addClass(`floatlabel-for-class-${className}`);
					}
				});
			}

			this._themeCssClasses = classes;
		}
	}

	// ------- css class updating END --------

	setupSearchClearBtn() {
		const dropdown = this._selectInstance?.$dropdown;

		if (!dropdown) {
			return;
		}

		const searchContainer = dropdown.find('.select2-search');

		if (!searchContainer.length) {
			return;
		}

		// replace font icon with UiIcon for search
		if (!searchContainer.find('ui-icon[name="Search"]').length) {
			searchContainer.prepend('<ui-icon name="Search" size="sm"></ui-icon>');
		}

		const searchInput = searchContainer.find('input'),
			searchBtnClearClass = 'btn-search-clear';

		if (!searchContainer.find(`.${searchBtnClearClass}`).length) {
			const searchBtnClearEl = $(
				`<a role="button" class="${searchBtnClearClass}"><ui-icon name="Close" size="xs"></ui-icon></a>`,
			);

			searchBtnClearEl.on(
				'click',
				bind(this, () => {
					dropdown.removeClass(this.select2SearchBtnClearClass);
					this._selectInstance?.trigger('query', { term: '' }); // reset and show all dropdown options
					searchInput.val('').focus(); // clearing input text
					return false;
				}),
			);

			searchContainer.append(searchBtnClearEl);

			searchInput.on(
				'input',
				bind(this, () => {
					dropdown.toggleClass(
						this.select2SearchBtnClearClass,
						searchInput.val()!.length > 0,
					);
				}),
			);
		}
	}

	setupClearSelectionBtn() {
		const dropdownContainer = this._selectInstance!.$dropdown,
			dropdown = dropdownContainer.find('.select2-dropdown'),
			resultsContainer = dropdownContainer.find('.select2-results');

		if (!dropdownContainer || !dropdown || !resultsContainer) {
			return;
		}

		const currentVal = this._selectElement?.val(),
			selectionCount = Array.isArray(currentVal) ? currentVal.length : currentVal ? 1 : 0,
			hasCurrentSelection = selectionCount > 0;

		const existingBtn = dropdown.find(`.${this.select2ClearSelectionBtnClass}`);

		if (hasCurrentSelection) {
			const clearSelectionText = `Clear selection (${selectionCount})`;

			dropdown.addClass(this.select2DropdownWithClearClass);

			// if button already exists, just update the text
			if (existingBtn.length) {
				existingBtn.contents().last().replaceWith(clearSelectionText);
				return;
			}

			// create the clear selection button element
			const clearSelectionBtnEl = $(
				`<a role="button" class="${this.select2ClearSelectionBtnClass}">
					<ui-icon name="Close" size="sm"></ui-icon>
					${clearSelectionText}
				</a>`,
			);

			// bind click handler
			clearSelectionBtnEl.on(
				'click',
				bind(this, () => {
					// clear the select2 selection first
					if (this._selectElement) {
						this._selectElement.val(this.isMultiple ? [] : '').trigger('change');
					}

					this._selectElement?.select2('close');
					return false;
				}),
			);

			// append after the results container (as sibling, not child)
			resultsContainer.after(clearSelectionBtnEl);
		} else {
			// no selection - remove button and class if they exist
			dropdown.removeClass(this.select2DropdownWithClearClass);
			existingBtn?.remove();
		}
	}

	updateShownTags() {
		const maxTagsShown = this.maxTagsShown!,
			selectedCount = (this._selectElement?.val() as ValueMultiple).length,
			hiddenTagsCount = selectedCount - maxTagsShown,
			tagsContainerEl = this._selectInstance?.$selection.find('.select2-selection__rendered');

		if (tagsContainerEl && hiddenTagsCount > 0) {
			tagsContainerEl
				.find('li.select2-selection__choice:not(.counter)')
				.each((i: number, tag: HTMLElement) => {
					if (i >= maxTagsShown) {
						$(tag).addClass('select2-selection__choice--hidden');
					}
				});

			tagsContainerEl
				.find('.select2-search')
				.before(`<li class="select2-selection__choice counter">+${hiddenTagsCount}</li>`);
		}
	}

	@action
	updateDropdownSizes(e?: Event | JQuery.TriggeredEvent) {
		const selectInstance = this._selectInstance;

		if (!selectInstance) {
			return;
		}

		const dropdown = selectInstance.$dropdown,
			container = selectInstance.$container;

		if (!dropdown || !container) {
			return;
		}

		// update dropdown max height
		try {
			if (!selectInstance.isOpen()) {
				return;
			}

			if (e && e.type !== 'select2:open' && e.target === container.get(0)) {
				return;
			}

			const win = $(window),
				options = dropdown.find('.select2-results > .select2-results__options'),
				padding = 20,
				winHeight = win.height()!,
				scrollTop = win.scrollTop()!,
				availableHeightAbove = container.offset()!.top - scrollTop,
				availableHeightBelow =
					winHeight + scrollTop - container.offset()!.top - container.height()!;

			let availableHeight = Math.max(availableHeightAbove, availableHeightBelow);

			availableHeight = Math.min(winHeight * 0.7, availableHeight);

			availableHeight -= options.position().top + padding;

			// account for clear selection button if present
			const clearSelectionBtn = dropdown.find(`.${this.select2ClearSelectionBtnClass}`);
			if (clearSelectionBtn.length) {
				availableHeight -= clearSelectionBtn.outerHeight() || 0;
			}

			options.css({ maxHeight: availableHeight });

			selectInstance.dropdown._positionDropdown();
		} catch (_e) {
			// skip
		}

		// calculate dropdown overlay width
		if (this.args.isDropdownWider) {
			try {
				dropdown.find('.overlay').width(container.width()! - 2);
			} catch (_e) {
				// skip
			}
		}
	}

	// triggers when selected value changes from select itself
	// if it is an actual change, send it upwards
	@action
	onChange() {
		const currentValue = this._selectElement?.val();

		let value;
		// select has always string value(s) - must cast before returning
		if (this.isNumeric) {
			if (this.isMultiple) {
				value = (currentValue as ValueMultiple).map((v) => +v);
			} else if (currentValue) {
				// only cast to int when there IS a value
				value = +currentValue;
			} else {
				value = null;
			}
		} else {
			value = currentValue;
		}

		const selectedString =
			this.args.selected || this.args.selected === 0 ? String(this.args.selected) : '';

		// avoid complicated casting, compare strings instead
		if (selectedString !== String(value)) {
			(this.args.onChange as (value: M['selected']) => void)(value);
			this.resetTooltipTextForSelectionCache();
		}
	}

	// triggers when args.options change from outside (e.g. when item gets globally deleted)
	// repopulate the dropdown fom scratch (can be optimized)
	@action
	updateSelectOptions() {
		this.options = this._parseOptions((this.args.options || []) as OptionRaw<T>[]);

		this.resetTooltipTextForSelectionCache();

		const selectInstance = this._selectInstance;
		if (selectInstance) {
			const el = this._selectElement;

			if (el) {
				const value = el.val()!;

				// TODO: this could use some perf improvement
				el.find('option:not(.placeholder)').remove();
				selectInstance.dataAdapter.addOptions(
					selectInstance.dataAdapter.convertToOptions(this.options),
				);

				el.val(value); // keep the same selected, but dont trigger change

				// an open list is not redrawn by new options (e.g. options disabled by a max selection), so re-query it
				if (this.isMultiple && this.args.refreshOpenDropdown && selectInstance.isOpen()) {
					const results = selectInstance.$dropdown.find('.select2-results__options');
					const scrollTop = results.scrollTop() ?? 0;
					// in multi mode the search input is inline in the selection, not in the dropdown
					const search = selectInstance.selection?.$search;
					const term = search?.val();
					selectInstance.trigger('query', { term: term || '' });
					// the re-query clears the "max selected" message, and our custom `query` (compat/query)
					// bypasses MaximumSelectionLength.query, which would normally re-check the max
					selectInstance.dataAdapter._checkIfMaximumSelected?.();
					results.scrollTop(scrollTop);
				}
			}
		}
	}

	// triggers when args.selected change from outside
	@action
	updateSelected() {
		// update selected only after loading is finished
		if (this.args.isLoading) {
			return;
		}

		const el = this._selectElement;

		if (el) {
			const currentValue = el.val(),
				newValue = this.args.selected as string | string[] | number; // this assertion is quite wonky tbh

			// avoid complicated casting, compare strings instead
			if (String(currentValue) !== String(newValue)) {
				el.val(newValue);
				let valueOnElement = el.val();

				// check if actually selected - it won't be if the value is not in options
				// so try to add it to options. only do it once.
				if (newValue && !equal(newValue, valueOnElement)) {
					this.updateSelectOptions();
					el.val(newValue);
					valueOnElement = el.val();
				}

				if (newValue && !equal(newValue, valueOnElement)) {
					// ignore it
				} else {
					el.trigger('change');
					this.resetTooltipTextForSelectionCache();
				}
			}
		}
	}

	// triggers when args.floatlabel or args.placeholder change
	@action
	updateFloatlabel(element: HTMLElement) {
		const wrapper = $(element).closest('.floatlabel-wrapper');
		const label = wrapper.find('.label-floatlabel');
		if (label) {
			label.text(this.floatlabelText);
		}
	}
	<template>
		<select
			class="custom-select select2-hidden-accessible {{this.themeClass}} {{this.sizeClass}}"
			id={{this.uniqueId}}
			...attributes
			required={{@isRequired}}
			{{! @glint-ignore: placeholder is not typed on HTMLSelectElement (used by select2) }}
			placeholder={{@placeholder}}
			data-label={{this.floatlabelText}}
			disabled={{this.isDisabled}}
			{{! @glint-ignore: the method bind }}
			onchange={{this.onChange}}
			multiple={{this.isMultipleOrCustomAdd}}
			{{didInsert this.setupSelect}}
			{{didUpdate this.updateSelectOptions @options}}
			{{didUpdate this.updateSelected @selected @isLoading}}
			{{didUpdate this.recreateSelect @maxSelectionLength}}
			{{didUpdate
				this.updateThemeCssClasses
				@theme
				@size
				@isMultiple
				@extraClass
				@onCustomAdd
				@isDropdownWider
				@isLoading
			}}
			{{didUpdate this.updateFloatlabel @selected @placeholder @floatlabelText}}
			{{willDestroy this.teardownSelect}}
			{{onWindowEvents this.updateDropdownSizes "scroll resize orientationchange" 200}}
		>

			{{#if this.showEmptyPlaceholder}}
				<option class="placeholder"></option>
			{{/if}}

			{{#each this.initialSelection as |selected|}}
				<option value={{selected.id}} selected="selected">{{selected.text}}</option>
			{{/each}}

		</select>
	</template>
}
